import { QueryClient } from '@tanstack/react-query';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { addBusinessDays } from 'dashboard409a/components/OperationsModal/useOperationsModal';
import { EngagementCategory, ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { updateValuationStatus } from 'dashboard409a/services/attio-common';
import { getAttioValuationDetail } from 'dashboard409a/services/attio-operation';
import { FulfillmentQueryKeys } from 'dashboard409a/services/fulfillment';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import {
  buildEnsureFulfillmentBaseRequest,
  mergeFulfillmentSideEffectsIntoEnsureRequest,
} from './ensureFulfillmentFromValuationDetail';
import { getFulfillmentSideEffectsForStatusChange } from './fulfillmentOnStatusChange';

export function getSupportingDueDateForValuationStatusChange(
  statusLabel: string,
  engagementCategory: string | undefined
): string {
  let supportingDueDate = addBusinessDays(new Date(), 4);
  if (statusLabel === ValuationStatus.CLIENT_APPROVAL_RECEIVED) {
    supportingDueDate = addBusinessDays(new Date(), 3);
    if (engagementCategory === EngagementCategory.EQUITY_COMPENSATION) {
      supportingDueDate = addBusinessDays(new Date(), 2);
    }
  }
  return supportingDueDate;
}

export type EnsureFulfillmentBeforeStatusPatchParams = {
  valuationId: string;
  valuationDetail: AttioValuationDetailResponse;
  modalData: unknown;
  statusLabel: string;
  existingFulfillment: Fulfillment | null;
  companyMeasurementDateId: number | undefined;
  appCompanyId: string | undefined;
};

export type EnsureFulfillmentBeforeStatusPatchResult = { ok: true } | { ok: false; reason: 'missing_valuation_date' };

/** POST ensure-fulfillment-for-attio-valuation — same contract as Operations modal. */
export async function ensureFulfillmentBeforeAttioValuationStatusPatch(
  params: EnsureFulfillmentBeforeStatusPatchParams
): Promise<EnsureFulfillmentBeforeStatusPatchResult> {
  const {
    valuationId,
    valuationDetail,
    modalData,
    statusLabel,
    existingFulfillment,
    companyMeasurementDateId,
    appCompanyId,
  } = params;

  const ensureBase = buildEnsureFulfillmentBaseRequest({
    valuationDetail,
    valuationId,
    modalData,
  });
  if (!ensureBase) {
    return { ok: false, reason: 'missing_valuation_date' };
  }

  const supportingDueDate = getSupportingDueDateForValuationStatusChange(
    statusLabel,
    valuationDetail.valuedInformation?.engagementCategory
  );

  const sideEffects = getFulfillmentSideEffectsForStatusChange({
    statusLabel,
    existingFulfillment,
    companyMeasurementDateId,
    appCompanyId,
    supportingDueDate,
  });

  const ensureBody = mergeFulfillmentSideEffectsIntoEnsureRequest(ensureBase, sideEffects);
  await ApiService.apiDashboardOperationsEnsureFulfillmentForAttioValuationCreate(ensureBody);
  return { ok: true };
}

/**
 * Loads valuation detail + CMD + fulfillment slice for workboard contexts that do not
 * already have modal/hook data. Uses direct API calls so optimistic VALUATION_ITEM cache is not overwritten mid-flow.
 */
export async function loadFulfillmentContextForAttioValuationStatusChange(valuationId: string): Promise<{
  valuationDetail: AttioValuationDetailResponse;
  companyMeasurementDateId: number | undefined;
  existingFulfillment: Fulfillment | null;
  appCompanyId: string | undefined;
}> {
  const valuationDetail = await getAttioValuationDetail(valuationId);
  const companyMeasurementDate = await ApiService.apiCompanyMeasurementDatesRead(valuationId);
  const companyMeasurementDateId = companyMeasurementDate?.id;
  const appCompanyId = companyMeasurementDate?.company?.id?.toString();

  let existingFulfillment: Fulfillment | null = null;
  if (appCompanyId != null && companyMeasurementDateId != null) {
    const fulfillmentResponse = await ApiService.apiDashboardCompanyOperationsFulfillmentList(appCompanyId);
    existingFulfillment =
      fulfillmentResponse?.results?.find(f => f.company_measurement_date === companyMeasurementDateId) ?? null;
  }

  return {
    valuationDetail,
    companyMeasurementDateId,
    existingFulfillment,
    appCompanyId,
  };
}

export async function invalidateCachesAfterSuccessfulEnsureFulfillment(
  queryClient: QueryClient,
  valuationId: string
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
  });
  await queryClient.invalidateQueries({
    queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST],
    exact: false,
  });
}

export async function invalidateCachesAfterSuccessfulValuationStatusUpdate(
  queryClient: QueryClient,
  valuationId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId], exact: false }),
    queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS], exact: false }),
    queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.CURRENT_VALUATIONS], exact: false }),
    queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.PAST_VALUATIONS], exact: false }),
    queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATIONS], exact: false }),
    queryClient.invalidateQueries({ queryKey: [AttioQueryKeys.VALUATIONS_ALL], exact: false }),
  ]);
}

export type FulfillmentContextForValuationStatusChange = {
  valuationDetail: AttioValuationDetailResponse;
  companyMeasurementDateId: number | undefined;
  existingFulfillment: Fulfillment | null;
  appCompanyId: string | undefined;
};

export type RunValuationStatusTransitionParams = {
  valuationId: string;
  statusId: string;
  statusLabel: string;
  queryClient: QueryClient;
  modalData: unknown;
  fulfillmentContext?: FulfillmentContextForValuationStatusChange;
  onAfterEnsureSuccess?: () => Promise<void> | void;
  companyName?: string;
};

export type RunValuationStatusTransitionResult =
  | { ok: true }
  | { ok: false; reason: 'missing_valuation_date' | 'load_failed' | 'ensure_failed' | 'status_patch_failed' };

/**
 * Unified status transition flow:
 * 1) Load fulfillment context (unless already provided),
 * 2) Ensure fulfillment from valuation detail,
 * 3) Patch Attio valuation status,
 * 4) Invalidate caches in consistent order.
 */
export async function runValuationStatusTransition(
  params: RunValuationStatusTransitionParams
): Promise<RunValuationStatusTransitionResult> {
  const { valuationId, statusId, statusLabel, queryClient, modalData, onAfterEnsureSuccess, companyName } = params;

  let context = params.fulfillmentContext;
  if (!context) {
    try {
      context = await loadFulfillmentContextForAttioValuationStatusChange(valuationId);
    } catch {
      return { ok: false, reason: 'load_failed' };
    }
  }

  try {
    const ensureResult = await ensureFulfillmentBeforeAttioValuationStatusPatch({
      valuationId,
      valuationDetail: context.valuationDetail,
      modalData,
      statusLabel,
      existingFulfillment: context.existingFulfillment,
      companyMeasurementDateId: context.companyMeasurementDateId,
      appCompanyId: context.appCompanyId,
    });
    if (!ensureResult.ok) {
      return ensureResult;
    }
  } catch {
    return { ok: false, reason: 'ensure_failed' };
  }

  await invalidateCachesAfterSuccessfulEnsureFulfillment(queryClient, valuationId);
  await onAfterEnsureSuccess?.();

  try {
    await updateValuationStatus(valuationId, statusId, undefined, companyName);
  } catch {
    return { ok: false, reason: 'status_patch_failed' };
  }

  await invalidateCachesAfterSuccessfulValuationStatusUpdate(queryClient, valuationId);
  return { ok: true };
}
